import axios from 'axios';
import { createAuthHeaders } from '../../api-client';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

export const databaseApi = {
  /**
   * Execute a SQL query against the OptiDev Cloud database
   */
  async executeQuery(
    token: string,
    projectId: string,
    sql: string,
  ): Promise<{ rows: any[]; rowCount: number; fields: any[] }> {
    const response = await axios.post(
      `${API_URL}/api/supabase/projects/${projectId}/database/query`,
      { sql },
      {
        headers: createAuthHeaders(token),
      },
    );
    return response.data;
  },

  /**
   * Get list of tables in the database
   */
  async getTables(
    token: string,
    projectId: string,
  ): Promise<{ tables: any[] }> {
    const response = await axios.get(
      `${API_URL}/api/supabase/projects/${projectId}/database/tables`,
      {
        headers: createAuthHeaders(token),
      },
    );
    return response.data;
  },

  /**
   * Get table schema (columns, types, constraints)
   */
  async getTableSchema(
    token: string,
    projectId: string,
    tableName: string,
  ): Promise<{ schema: any[] }> {
    const response = await axios.get(
      `${API_URL}/api/supabase/projects/${projectId}/database/tables/${tableName}/schema`,
      {
        headers: createAuthHeaders(token),
      },
    );
    return response.data;
  },

  /**
   * Update a single cell in a table
   */
  async updateCell(
    token: string,
    projectId: string,
    tableName: string,
    rowId: any,
    columnName: string,
    newValue: any,
  ): Promise<{ success: boolean }> {
    const response = await axios.post(
      `${API_URL}/api/supabase/projects/${projectId}/database/update-cell`,
      { tableName, rowId, columnName, newValue },
      {
        headers: createAuthHeaders(token),
      },
    );
    return response.data;
  },

  /**
   * Add a new row to a table
   */
  async addRow(
    token: string,
    projectId: string,
    tableName: string,
    rowData: Record<string, any>,
  ): Promise<{ success: boolean; row: any }> {
    const response = await axios.post(
      `${API_URL}/api/supabase/projects/${projectId}/database/add-row`,
      { tableName, rowData },
      {
        headers: createAuthHeaders(token),
      },
    );
    return response.data;
  },

  /**
   * Reset database password and get new connection string
   */
  async resetDatabasePassword(
    token: string,
    projectId: string,
  ): Promise<{ success: boolean; connectionString: string }> {
    const response = await axios.post(
      `${API_URL}/api/supabase/projects/${projectId}/database/reset-password`,
      {},
      {
        headers: createAuthHeaders(token),
      },
    );
    return response.data;
  },

  /**
   * Get tables with realtime enabled
   */
  async getRealtimeTables(
    token: string,
    projectId: string,
  ): Promise<{ tables: string[] }> {
    const response = await axios.get(
      `${API_URL}/api/supabase/projects/${projectId}/database/realtime-tables`,
      {
        headers: createAuthHeaders(token),
      },
    );
    return response.data;
  },

  /**
   * Enable or disable realtime for a table
   */
  async setTableRealtime(
    token: string,
    projectId: string,
    tableName: string,
    enabled: boolean,
  ): Promise<{ success: boolean }> {
    const response = await axios.post(
      `${API_URL}/api/supabase/projects/${projectId}/database/realtime`,
      { tableName, enabled },
      {
        headers: createAuthHeaders(token),
      },
    );
    return response.data;
  },
};
